import { Component, Input, OnInit, OnDestroy, forwardRef, ElementRef, ViewContainerRef, ViewChild, inject, Injector, afterNextRender, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormGroup, Validators, NgControl } from '@angular/forms';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, first, map } from 'rxjs/operators';
import * as shajs from 'sha.js';
import { NxFormfieldControl, NxFormfieldUpdateEventType } from '@aposin/ng-aquila/formfield';
import { TrackService } from 'src/app/services/track.service';
import { SharedService } from 'src/app/shared/shared.service';
import { NxMaskDirective } from '@aposin/ng-aquila/mask';
import { ValidationService } from 'src/app/services/validation.service';

@Component({
  selector: 'app-phone-numbers',
  templateUrl: './phone-numbers.component.html',
  styleUrls: ['./phone-numbers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneNumbersComponent),
      multi: true
    },
    {
      provide: NxFormfieldControl,
      useExisting: forwardRef(() => PhoneNumbersComponent),
      multi: true
    }
  ]
})
export class PhoneNumbersComponent implements OnInit, OnDestroy, ControlValueAccessor, NxFormfieldControl<any> {
  @Input() formDataPhonesNumbers: FormGroup;
  @Input() mobileCode: FormControl 
  @Input() mobilePhone: FormControl 
  @Input() trackingPath?: string;
  @Input() saveOffer?: any;
  @Input() showExample?: any;

  phoneCodes = [];
  phoneMask = '00 000 0000';
  deactivateMask = false;
  phoneSeparator = [' '];
  oldMobileCode: string;
  oldMobilePhone: string;
  @ViewChild(NxMaskDirective) nxMaskDirective!: NxMaskDirective;
  injector = inject(Injector);

  // NxFormfieldControl properties
  stateChanges = new Subject<void>();
  @Input() id: string = 'phone-numbers';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() errorState: boolean = false;

  private _onChange: (value: any) => void = () => {};
  private _onTouched: () => void = () => {};

  constructor(private _elementRef: ElementRef, private trackService: TrackService, private sharedService: SharedService, private el: ElementRef,
  private validationService: ValidationService) {}
  value: any;
  empty: boolean;
  ngControl: NgControl;
  focused: boolean;
  readonly: boolean;
  shouldLabelFloat?: boolean;
  controlType?: string;
  updateOn?: NxFormfieldUpdateEventType;
  setAriaLabel?(value: string): void {
    throw new Error('Method not implemented.');
  }

  ngOnInit(): void {
    this.loadPhoneCodes();
    // this.initializeMobileCode();
    this.setupMobileCodeChangeHandler(true);
    
    
  }

  ngOnDestroy() {
    this.validationService.registerControl(this.mobilePhone, false);
    this.stateChanges.complete();
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    if (value) {
      this.mobileCode.setValue(value.mobileCode);
      this.mobilePhone.setValue(value.mobilePhone);
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.mobileCode.disable({ emitEvent: false });
      this.mobilePhone.disable({ emitEvent: false });
    } else {
      this.mobileCode.enable({ emitEvent: false });
      this.mobilePhone.enable({ emitEvent: false });
    }
  }

  // NxFormfieldControl methods
  setDescribedByIds(ids: string[]): void {
    const controlElement = this._elementRef.nativeElement.querySelector('input');
    if (controlElement) {
      controlElement.setAttribute('aria-describedby', ids.join(' '));
    }
  }

  onContainerClick(event: MouseEvent): void {
    const inputElement = this._elementRef.nativeElement.querySelector('input');
    if (inputElement) {
      inputElement.focus();
    }
  }

    _onInputBlur() {
        this.focused = false;
      
  }

  _onInputFocus() {
    this.focused = true;
  }


  get elementRef(): ElementRef<any> {
    return this._elementRef;
  }

  private loadPhoneCodes(): void {
    this.sharedService.getPhoneCodes().pipe(
      first(),
      finalize(() => {
        this.initializeMobileCode();
      })
    ).subscribe(
      (res) => {
        this.phoneCodes = res;
      }
    );
  }

  private initializeMobileCode(): void {
    if (this.mobileCode.value === '' || this.mobileCode.value == undefined) {
      this.setDefaultMobileCode();
    }

    if (this.mobilePhone?.value?.length > 9) {
      this.deactivateMask = true;
      this.mobilePhone.setValue(this.nxMaskDirective.getUnmaskedValue());
      if (this.deactivateMask) {
        afterNextRender(
          () =>
            this.mobilePhone.setValue(this.nxMaskDirective.getUnmaskedValue()),
          { injector: this.injector }
        );
      }

    }
  }

  private setDefaultMobileCode(): void {
    this.mobileCode.setValue('+' + this.phoneCodes[0].value);
    this.mobilePhone.setValidators(Validators.pattern('[0-9 ]{9,9}'));
  }

  private setupMobileCodeChangeHandler(initial?:boolean): void {
      if (this.mobileCode.value !== '+359') {
        this.deactivateMask = true;       
       this.mobilePhone.setValue(this.nxMaskDirective.getUnmaskedValue());
        if (this.deactivateMask) {
          afterNextRender(
            () =>
              this.mobilePhone.setValue(this.nxMaskDirective.getUnmaskedValue()),
            { injector: this.injector }
          );
        }
        this.mobilePhone.setValidators(Validators.pattern('[0-9 ]{0,15}'));
      } else if (this.mobileCode.value === undefined) {
        this.setDefaultMobileCode();
      } else {
        this.deactivateMask = false;
        this.mobilePhone.setValidators(Validators.pattern('[0-9 ]{9,9}'));
      }
      this.mobilePhone.updateValueAndValidity(
      );
      if(!initial){
        (this.mobilePhone.status=='INVALID' && this.saveOffer!=='true') ? this.errorState=true: this.errorState = false;
        this.validationService.errorPhone$.next(this.errorState);
      }

  
  }

  trackMobilePhone(): void {
    if (this.hasMobilePhoneChanged()) {
      this.handleMobilePhoneChange();
    }
    if (this.mobilePhone.status=='INVALID' && this.saveOffer!=='true')  {
      this.errorState=true;
      this.validationService.errorPhone$.next(true)
    }
    else {
      this.errorState = false;
      this.validationService.errorPhone$.next(false)
    }
  }

  private hasMobilePhoneChanged(): boolean {
    return this.oldMobileCode !== this.mobileCode.value || this.oldMobilePhone !== this.mobilePhone.value;
  }

  private handleMobilePhoneChange(): void {
    if (this.mobileCode.value === '+359') {
      this.deactivateMask = false;
      this.mobilePhone.setValidators(Validators.pattern('[0-9 ]{9,9}'));
    }
    else{
      this.deactivateMask = true;
      this.mobilePhone.setValue(this.nxMaskDirective.getUnmaskedValue());
      if (this.deactivateMask) {
        afterNextRender(
          () =>
            this.mobilePhone.setValue(this.nxMaskDirective.getUnmaskedValue()),
          { injector: this.injector }
        );
      }      this.mobilePhone.setValidators(Validators.pattern('[0-9 ]{0,15}'));
    }

    (this.mobilePhone.status=='INVALID' && this.saveOffer!=='true') ? this.errorState=true: this.errorState = false;
    this.validationService.errorPhone$.next(this.errorState);

    this.trackService.checkErrors(this.mobilePhone, 'Mobile phone');
    this.trackService.checkErrors(this.mobileCode, 'Mobile code');

    if (this.trackingPath) {
      const hashedMobilePhone = shajs('sha256').update(this.mobileCode.value + this.mobilePhone.value).digest('hex');
      this.trackService.pushEventTracking(this.trackingPath, hashedMobilePhone);
      this.oldMobileCode = this.mobileCode.value;
      this.oldMobilePhone = this.mobilePhone.value;
    }
  }
}


